import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import type { LeaveBalanceRow } from "./LeaveInsightCards.js";
import { availableDays } from "../lib/leaveBalance.js";
import { cn } from "../lib/utils.js";
import { Badge } from "./ui/badge.js";
import { Pagination } from "./ui/pagination.js";
import { Button } from "./ui/button.js";
import { Textarea } from "./ui/textarea.js";

interface LeaveRequest {
  id: string;
  leaveType: string;
  startDate: string;
  endDate:   string;
  days:      number | string;
  halfDay:   boolean;
  halfDayPeriod: string | null;
  reason:    string | null;
  isWfh:     boolean;
  isLop:     boolean;
  lopDays:   number | string | null;
  lateFlag:  boolean;
  isRetroactive: boolean;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string };
}

interface Props {
  requests:  LeaveRequest[];
  onRefresh: () => void;
  balances?: Record<string, LeaveBalanceRow[]>;
  /** Narrows the queue to requests whose employee name or code matches every word. */
  search?:   string;
}

const DEFAULT_PAGE_SIZE = 10;

function matchesSearch(r: LeaveRequest, terms: string[]) {
  const haystack = `${r.employee.firstName} ${r.employee.lastName} ${r.employee.employeeCode}`.toLowerCase();
  return terms.every((t) => haystack.includes(t));
}

function fmt(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function daysLabel(req: LeaveRequest) {
  const d = Number(req.days);
  if (req.halfDay) return `0.5 day · half (${req.halfDayPeriod === "MORNING" ? "morning" : "afternoon"})`;
  return `${d % 1 === 0 ? d : d.toFixed(1)} ${d === 1 ? "day" : "days"}`;
}

function availableFor(rows: LeaveBalanceRow[] | undefined, leaveType: string): number | null {
  const b = rows?.find((x) => x.leaveType === leaveType);
  if (!b) return null;
  return availableDays(b);
}

export function PendingApprovalsQueue({ requests: allRequests, onRefresh, balances, search = "" }: Props) {
  const [rejectingId,  setRejectingId]  = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy,         setBusy]         = useState<string | null>(null);
  const [err,          setErr]          = useState("");
  const [expanded,     setExpanded]     = useState<Set<string>>(new Set());
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(DEFAULT_PAGE_SIZE);

  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  const requests = terms.length ? allRequests.filter((r) => matchesSearch(r, terms)) : allRequests;

  // A new search starts from the first page with no half-typed rejection left open.
  useEffect(() => { setPage(1); setRejectingId(null); setRejectReason(""); setErr(""); }, [search]);

  const toggleExpanded = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function doApprove(id: string) {
    setBusy(id); setErr("");
    try {
      await api.patch(`/leave/requests/${id}/approve`);
      onRefresh();
    } catch (err) { logError("PendingApprovalsQueue.doApprove", err); setErr("Failed to approve — try again"); }
    setBusy(null);
  }

  async function doReject(id: string) {
    if (!rejectReason.trim()) { setErr("Enter a rejection reason"); return; }
    setBusy(id); setErr("");
    try {
      await api.patch(`/leave/requests/${id}/reject`, { rejectionReason: rejectReason.trim() });
      setRejectingId(null); setRejectReason("");
      onRefresh();
    } catch (err) { logError("PendingApprovalsQueue.doReject", err); setErr("Failed to reject — try again"); }
    setBusy(null);
  }

  if (!requests.length) {
    return <div className="py-5 text-center text-[13px] text-surface-400">{terms.length ? "No pending approvals match your search" : "No pending approvals"}</div>;
  }

  const resetTransient = () => { setRejectingId(null); setRejectReason(""); setErr(""); };

  // Paged in the browser over the requests already loaded. After an approve/reject
  // shrinks the list, the page is clamped so it never lands past the last one.
  const totalPages = Math.max(1, Math.ceil(requests.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visible = requests.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // A reason this long is clamped to one line with a More toggle; shorter ones show in full.
  const LONG_REASON = 70;

  return (
    <div>
      {err && <div className="mb-2.5 rounded-lg bg-[var(--color-error-bg)] px-3 py-2 text-[13px] text-[var(--color-error-fg)]">{err}</div>}

      <div className="grid items-start gap-2 lg:grid-cols-2">
        {visible.map((r) => {
          const type = r.leaveType.replace(/_/g, " ");
          const avail = availableFor(balances?.[r.employee.id], r.leaveType);
          const long = (r.reason?.length ?? 0) > LONG_REASON;
          const open = expanded.has(r.id);
          const rejecting = rejectingId === r.id;
          return (
            <div key={r.id} className="rounded-[10px] border border-surface-200 bg-surface-0 px-3 py-2.5">
              {/* Who + flags on the left, the decision on the right */}
              <div className="flex items-center gap-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                  <span className="truncate text-sm font-semibold text-surface-900">{r.employee.firstName} {r.employee.lastName}</span>
                  <span className="text-[11px] text-surface-400">{r.employee.employeeCode}</span>
                  {r.lateFlag && !r.isRetroactive && <Badge tone="warning" className="px-[7px] py-px text-[10px] font-bold">LATE</Badge>}
                  {r.isRetroactive && <Badge tone="warning" className="px-[7px] py-px text-[10px] font-bold">RETRO</Badge>}
                  {r.isLop && <Badge tone="error" className="px-[7px] py-px text-[10px] font-bold">LOP</Badge>}
                </div>
                <div className="flex shrink-0 gap-1.5">
                  {rejecting ? (
                    <>
                      <Button size="sm" variant="destructive" disabled={busy === r.id} onClick={() => doReject(r.id)}>
                        {busy === r.id ? "Rejecting…" : "Confirm"}
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => { setRejectingId(null); setRejectReason(""); }}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" disabled={busy === r.id} onClick={() => doApprove(r.id)}>
                        {busy === r.id ? "Approving…" : "Approve"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[var(--color-error-border)] text-error hover:border-[var(--color-error-border)] hover:bg-[var(--color-error-bg)]"
                        onClick={() => { setRejectingId(r.id); setErr(""); }}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Type · dates · days · balance, on one line that wraps only when it must */}
              <div className="mt-1 text-[12.5px] text-surface-600">
                <span className="font-medium text-surface-800">{type}</span>
                {" · "}{r.startDate === r.endDate ? fmt(r.startDate) : `${fmt(r.startDate)} – ${fmt(r.endDate)}`}
                {" · "}{daysLabel(r)}
                {r.isLop && r.lopDays && <span className="text-error"> ({Number(r.lopDays)} LOP)</span>}
                {avail !== null && (
                  <span className={avail < Number(r.days) ? "text-error" : "text-surface-500"}>
                    {" · "}{avail % 1 === 0 ? avail : avail.toFixed(2)} available
                  </span>
                )}
              </div>

              {r.reason && (
                <div className="mt-1 flex items-start gap-1.5 text-xs italic text-surface-500">
                  <span className={cn("min-w-0 flex-1 break-words", !open && long && "truncate")} title={long && !open ? r.reason : undefined}>"{r.reason}"</span>
                  {long && (
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => toggleExpanded(r.id)}
                      className="shrink-0 rounded text-[11px] font-semibold not-italic text-brand-600 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                    >
                      {open ? "Less" : "More"}
                    </button>
                  )}
                </div>
              )}

              {rejecting && (
                <Textarea
                  autoFocus
                  rows={2}
                  size="sm"
                  className="mt-2"
                  placeholder="Reason for rejection…"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>

      <Pagination
        className="mt-3"
        meta={{ total: requests.length, page: currentPage, limit: pageSize, totalPages }}
        onPageChange={(p) => { setPage(p); resetTransient(); }}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); resetTransient(); }}
        pageSizeOptions={[10, 25, 50]}
      />
    </div>
  );
}
